import { PUSH_CONTROLS_OVERLAY } from "./pushControlsOverlay";
import { PLAY_AGAIN_TRANSLATIONS } from "./playAgainTranslations";
import { CLUB_ELO_TRANSLATIONS } from "./clubEloTranslations";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { resolveInitialLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from "./languagePreference";
import en from "./i18n/en";
import el from "./i18n/el";
import fr from "./i18n/fr";
import de from "./i18n/de";
import it from "./i18n/it";
import es from "./i18n/es";
import ja from "./i18n/ja";
import { connectedTranslations } from "./i18n/connected";
import { I18N_OVERLAY } from "./i18nOverlay";
import { CONNECTED_PRIVACY_OVERLAY } from "./connectedPrivacyOverlay";
import { CONNECTED_MATCH_POLISH_OVERLAY } from "./connectedMatchPolishOverlay";
import { CONNECTED_MATCH_FORM_OVERLAY } from "./connectedMatchFormOverlay";
import { CONNECTED_CONFIRMATION_OVERLAY } from "./connectedConfirmationOverlay";
import { CONNECTED_RESOLUTION_OVERLAY } from "./connectedResolutionOverlay";
import { CONNECTED_DISCONNECT_OVERLAY } from "./connectedDisconnectOverlay";
import { CONNECTED_RATING_OVERLAY } from "./connectedRatingOverlay";
import { OPPONENT_RATING_STATS_OVERLAY } from "./opponentRatingStatsOverlay";
import { CONNECTED_DIRECT_INVITE_OVERLAY } from "./connectedDirectInviteOverlay";
import { LEAN_FAST_TRACK_OVERLAY } from "./leanFastTrackOverlay";
import { EQUIPMENT_STATS_OVERLAY } from "./equipmentStatsOverlay";
import { CLUB_PROFILE_OVERLAY } from "./clubProfileOverlay";
import { RANKINGS_OVERLAY } from "./rankingsOverlay";
import { ADMIN_ANALYTICS_OVERLAY } from "./adminAnalyticsOverlay";
import { NEW_MATCH_FUNNEL_OVERLAY } from "./newMatchFunnelOverlay";
import { PLAY_REVIEW_OVERLAY } from "./playReviewOverlay";
import { NOTIFICATION_CENTER_OVERLAY } from "./notificationCenterOverlay";
import { OPPONENT_MERGE_OVERLAY } from "./opponentMergeOverlay";
import { CLUB_MEMBERSHIP_MERGE_OVERLAY } from "./clubMembershipMergeOverlay";

const SUPPORTED = SUPPORTED_LANGUAGES;

// Initialise synchronously, before React's first render, so a new guest never
// sees the wrong locale. Only explicit changes persist via changeLanguage.
const stored = (() => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("i18nextLng");
  } catch {
    return null;
  }
})();
const browserLanguages =
  typeof navigator !== "undefined"
    ? [...(navigator.languages ?? []), navigator.language].filter(Boolean)
    : [];
const initialLng: SupportedLanguage = resolveInitialLanguage(stored, browserLanguages);

const mergeTranslation = (
  code: SupportedLanguage,
  base: any,
  overlay: any,
  connectedPrivacy: any,
  polish: any,
  formOverlay: any,
  confirmationOverlay: any,
  resolutionOverlay: any,
  disconnectOverlay: any,
  ratingOverlay: any,
  ratingStatsOverlay: any,
  directInviteOverlay: any,
  leanFastTrackOverlay: any,
  equipmentStatsOverlay: any,
  clubProfileOverlay: any,
  rankingsOverlay: any,
) => {
  const connectedBase = connectedTranslations[code] ?? connectedTranslations.en;
  return {
    ...base,
    ...PLAY_AGAIN_TRANSLATIONS[code],
    ...(PUSH_CONTROLS_OVERLAY[code] ?? {}),
    ...overlay,
    ...polish,
    ...(ADMIN_ANALYTICS_OVERLAY[code] ?? {}),
    ...(NEW_MATCH_FUNNEL_OVERLAY[code] ?? {}),
    ...(PLAY_REVIEW_OVERLAY[code] ?? {}),
    ...(NOTIFICATION_CENTER_OVERLAY[code] ?? {}),
    ...(OPPONENT_MERGE_OVERLAY[code] ?? {}),
    ...(CLUB_MEMBERSHIP_MERGE_OVERLAY[code] ?? {}),
    ...(leanFastTrackOverlay ?? {}),
    ...(equipmentStatsOverlay ?? {}),
    nav: {
      ...(base?.nav ?? {}),
      ...(overlay?.nav ?? {}),
      ...(rankingsOverlay?.nav ?? {}),
    },
    rankings: {
      ...(base?.rankings ?? {}),
      ...(rankingsOverlay?.rankings ?? {}),
    },
    publicChrome: {
      ...(base?.publicChrome ?? {}),
      ...(overlay?.publicChrome ?? {}),
    },
    stats: {
      ...(base?.stats ?? {}),
      ...(overlay?.stats ?? {}),
      ...(polish?.stats ?? {}),
      ...(ratingStatsOverlay?.stats ?? {}),
    },
    settings: {
      ...(base?.settings ?? {}),
      ...(overlay?.settings ?? {}),
      ...(clubProfileOverlay?.settings ?? {}),
    },
    match: {
      ...(base?.match ?? {}),
      ...(formOverlay?.match ?? {}),
    },
    connectedShared: {
      ...(base?.connectedShared ?? {}),
      ...(overlay?.connectedShared ?? {}),
      ...(polish?.connectedShared ?? {}),
    },
    connected: {
      ...connectedBase,
      ...(base?.connected ?? {}),
      ...(overlay?.connected ?? {}),
      ...(polish?.connected ?? {}),
      ...(confirmationOverlay?.connected ?? {}),
      ...(resolutionOverlay?.connected ?? {}),
      ...(disconnectOverlay?.connected ?? {}),
      ...(ratingOverlay?.connected ?? {}),
      ...(directInviteOverlay?.connected ?? {}),
      confirmation: {
        ...(connectedBase?.confirmation ?? {}),
        ...(base?.connected?.confirmation ?? {}),
        ...(overlay?.connected?.confirmation ?? {}),
        ...(polish?.connected?.confirmation ?? {}),
        ...(confirmationOverlay?.connected?.confirmation ?? {}),
        ...(resolutionOverlay?.connected?.confirmation ?? {}),
      },
      sender: {
        ...(connectedBase?.sender ?? {}),
        ...(base?.connected?.sender ?? {}),
        ...(overlay?.connected?.sender ?? {}),
        ...(polish?.connected?.sender ?? {}),
        ...(connectedPrivacy?.sender ?? {}),
      },
      recipient: {
        ...(connectedBase?.recipient ?? {}),
        ...(base?.connected?.recipient ?? {}),
        ...(overlay?.connected?.recipient ?? {}),
        ...(polish?.connected?.recipient ?? {}),
        ...(connectedPrivacy?.recipient ?? {}),
      },
    },
  };
};

const baseResources: Record<SupportedLanguage, any> = { en, el, fr, de, it, es, ja };
const resources = Object.fromEntries(
  SUPPORTED.map((code) => [
    code,
    {
      translation: mergeTranslation(
        code,
        baseResources[code],
        I18N_OVERLAY[code],
        CONNECTED_PRIVACY_OVERLAY[code],
        CONNECTED_MATCH_POLISH_OVERLAY[code],
        CONNECTED_MATCH_FORM_OVERLAY[code],
        CONNECTED_CONFIRMATION_OVERLAY[code],
        CONNECTED_RESOLUTION_OVERLAY[code],
        CONNECTED_DISCONNECT_OVERLAY[code],
        CONNECTED_RATING_OVERLAY[code],
        OPPONENT_RATING_STATS_OVERLAY[code],
        CONNECTED_DIRECT_INVITE_OVERLAY[code],
        LEAN_FAST_TRACK_OVERLAY[code],
        EQUIPMENT_STATS_OVERLAY[code],
        CLUB_PROFILE_OVERLAY[code],
        RANKINGS_OVERLAY[code],
      ),
    },
  ]),
);

for (const code of SUPPORTED) {
  const bundle = resources[code].translation;
  bundle.clubElo = CLUB_ELO_TRANSLATIONS[code];
  bundle.notificationCenter = { ...bundle.notificationCenter,
    clubsReviewTitle: bundle.clubElo.bellTitle, clubsReviewBody: bundle.clubElo.bellBody,
  };
}

i18n.use(initReactI18next).init({
  resources,
  lng: initialLng,
  fallbackLng: "en",
  supportedLngs: [...SUPPORTED],
  interpolation: { escapeValue: false },
  initAsync: false,
});

const originalChange = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (async (lng?: string, ...rest: any[]) => {
  const result = await originalChange(lng as any, ...rest);
  try {
    if (typeof window !== "undefined" && lng) localStorage.setItem("i18nextLng", lng);
  } catch {}
  return result;
}) as typeof i18n.changeLanguage;

export default i18n;

export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "el", label: "Ελληνικά", flag: "🇬🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
];

